import { BadgeCheck } from "lucide-react";

import { ComicPanel } from "@/components/comic";

import { GuestIdentity } from "./guestApi";
import { GuestProvider, guestProviders } from "./providers";

/**
 * What this page is presenting on the visitor's behalf, and how to add to it.
 *
 * <p>Their identities and nothing else. No host, no calendar, no workspace: those are things a
 * member has, and a visitor here is not one (D-43, D-45).</p>
 */
export function GuestIdentityRail({
  identities,
  onSignOut,
  /** What this deployment can actually complete a sign-in with. Injected so it is testable. */
  providers = guestProviders(),
}: {
  identities: GuestIdentity[];
  onSignOut: () => void;
  providers?: GuestProvider[];
}) {
  const proved = new Set(identities.map((i) => i.provider.toUpperCase()));
  // Only what this deployment brokers, minus what they have already proved. Offering to add a
  // provider with no sign-in path would 404 on click (D-85).
  const missing = providers.filter((p) => !proved.has(p.id.toUpperCase()));

  return (
    <aside className="space-y-4">
      <span className="px-1 font-display text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Your identities
      </span>

      {/*
        "Proved", not "signed in". Signing in is what a member does to reach an account, and a
        visitor here has none: no user, no profile, nothing that outlives ten minutes. Borrowing the
        word would promise something the page cannot give them.
      */}
      <ComicPanel title="Proved">
        <ul className="space-y-2">
          {identities.map((identity) => (
            <li key={identity.provider} className="rounded-xl border border-border bg-card px-3 py-2">
              <p className="text-sm font-medium">{PROVIDER_LABELS[identity.provider] ?? identity.provider}</p>
              {identity.email && (
                <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                  {identity.email_verified ? (
                    <>
                      <BadgeCheck className="h-3 w-3 text-emerald-700" aria-hidden />
                      <span className="truncate">{identity.email}</span>
                    </>
                  ) : (
                    // Microsoft's address arrives inside a signed token and is still nobody's word
                    // but theirs (D-6). Saying so here is the only place the visitor sees it.
                    <span className="truncate">{identity.email} (unverified)</span>
                  )}
                </p>
              )}
            </li>
          ))}
        </ul>

        {/*
          One standing line about reach in general (D-69). It says the same thing before anyone has
          asked for anything as it does after a request is turned away, which is exactly what makes
          it safe to show at all: a line that never changes cannot be read as a hint about what just
          happened, and a hint about what just happened is the leak (D-44).
        */}
        <p className="mt-3 text-[11px] text-muted-foreground">
          More proved identities, more people your agent can reach.
        </p>

        {missing.length > 0 && (
          <div className="mt-2 space-y-2">
            {missing.map((provider) => (
              <a
                key={provider.id}
                href={`/api/v1/marketplace/signin/${provider.id}`}
                className="flex w-full items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:-translate-y-px hover:border-brand-blue"
              >
                {`Add ${provider.label}`}
              </a>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={onSignOut}
          className="mt-3 text-[11px] text-muted-foreground underline hover:text-foreground"
        >
          End session
        </button>
      </ComicPanel>
    </aside>
  );
}

const PROVIDER_LABELS: Record<string, string> = {
  GOOGLE: "Google",
  MICROSOFT: "Microsoft",
  LINKEDIN: "LinkedIn",
};
